import React from 'react';
import { Alert, Platform, RefreshControl, ScrollView, TouchableOpacity, View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useDispatch } from 'react-redux';
import { useRoute } from '@react-navigation/native';

import api from '../../../utils/api';
import tw from '../../../styles/tailwind';
import Text from '../../../components/Text';
import { startImpersonation } from '../../../store/Reducers/impersonation';

type ResidentRow = {
  uuid: string;
  type: string;
  type_str: string;
  deleted_at: string | null;
  unit: { uuid: string; name: string; deleted_at: string | null } | null;
  community: { uuid: string; name: string; deleted_at: string | null } | null;
};

type DetailData = {
  user: { uuid: string; name: string; email: string; phone_number?: string };
  residents: ResidentRow[];
};

export default function UserDetailScreen() {
  const route = useRoute<any>();
  const dispatch = useDispatch<any>();
  const uuid = route.params?.uuid;

  const { data, isLoading, refetch } = useQuery<DetailData>({
    queryKey: ['meta.user', uuid],
    queryFn: () => api.get(`/admin/users/${uuid}`).then(r => r.data),
  });

  async function doImpersonate() {
    try {
      const res = await api.post(`/admin/users/${uuid}/impersonate`);
      dispatch(startImpersonation(res.data.token, res.data.user));
    } catch (e: any) {
      const msg = e?.response?.data?.message ?? 'No se pudo impersonar';
      if (Platform.OS === 'web') window.alert(msg);
      else Alert.alert('Error', msg);
    }
  }

  function onImpersonate() {
    const targetName = data?.user.name ?? '';
    const message = `Vas a iniciar sesión como "${targetName}". Podrás volver a tu sesión desde el banner superior.`;

    if (Platform.OS === 'web') {
      if (window.confirm(message)) doImpersonate();
      return;
    }

    Alert.alert('Iniciar sesión como', message, [
      { text: 'Cancelar', style: 'cancel' },
      { text: 'Continuar', onPress: doImpersonate },
    ]);
  }

  return (
    <SafeAreaView style={tw`flex-1 bg-background`}>
      <ScrollView
        contentContainerStyle={tw`p-4`}
        refreshControl={<RefreshControl onRefresh={refetch} refreshing={isLoading} />}
      >
        <View style={tw`bg-white rounded-lg p-4 mb-4`}>
          <Text style={tw`text-xl font-600 text-gray-800`}>{data?.user.name}</Text>
          <Text style={tw`text-gray-500`}>{data?.user.email}</Text>
          {data?.user.phone_number ? (
            <Text style={tw`text-gray-400`}>{data.user.phone_number}</Text>
          ) : null}
        </View>

        <TouchableOpacity
          onPress={onImpersonate}
          style={tw`bg-secondary rounded-lg py-3 mb-4`}
        >
          <Text style={tw`text-white text-center font-600`}>
            Iniciar sesión como este usuario
          </Text>
        </TouchableOpacity>

        <Text style={tw`text-gray-700 font-600 mb-2`}>Residencias</Text>

        {(data?.residents ?? []).length === 0 ? (
          <Text style={tw`text-gray-400`}>Sin residencias</Text>
        ) : (
          (data?.residents ?? []).map(r => (
            <View
              key={r.uuid}
              style={tw.style(
                `bg-white rounded p-3 mb-2`,
                r.deleted_at && `opacity-60`,
              )}
            >
              <View style={tw`row justify-between`}>
                <Text style={tw`text-gray-800 font-500`}>
                  {r.community?.name ?? '—'}
                </Text>
                {r.deleted_at ? (
                  <Text style={tw`text-xs text-red-500`}>Eliminada</Text>
                ) : null}
              </View>
              <Text style={tw`text-gray-500 text-sm`}>
                {r.unit?.name ?? '—'} · {r.type_str}
              </Text>
            </View>
          ))
        )}
      </ScrollView>
    </SafeAreaView>
  );
}
